import React, { useState, useEffect, useCallback, useRef } from "react";
import Select, { components } from "react-select";
import axiosPrivate from "../../hooks/axiosPrivate";
import { showErrorToast } from "../../utils/Toaster";
import { useLoader } from "../../context/Loader/LoaderProvider";
import Loader from "../../context/Loader/Loader";
import "./ProductionDashboard.css";

// ─── helpers - converts a number into Indian currency format.
const fmtINR = (val) => {
  const n = parseFloat(val);
  if (isNaN(n) || val === null || val === undefined) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
};

// converts a large amount into Lakhs.
const fmtCr = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return fmtINR(n);
};

const fmtNum = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? "—" : n.toLocaleString("en-IN");
};

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-GB");
};


const PERIOD_OPTIONS = [
  { value: "daily",   label: "Daily"   },
  { value: "weekly",  label: "Weekly"  },
  { value: "monthly", label: "Monthly" },
  { value: "custom",  label: "Custom Date Range" },
];

const AUTO_REFRESH_MS = 60_000;

// ──KPI config 
const makeKpis = (summary, prevPending, overdueRevisedPendingAmt) => [
  { label: "Total Dispatch Order Amt", value: fmtCr(summary.totalDispatchOrderAmt), sub: `Amount for dispatched orders`, colour: "kpi-green", isItalic: true },
  { label: "Pending Value", value: fmtCr(summary.totalPendingAmt), sub: "100% of order value outstanding", colour: "kpi-orange", isItalic: true },
  { label: "Total Dispatch Order Qty", value: fmtNum(summary.totalDispatchOrderQty), sub: `Units dispatched`, colour: "kpi-green" },
  { label: "Pending Qty", value: fmtNum(summary.totalPendingQty), sub: "units still pending", colour: "kpi-blue" },
  { label: "Overdue Pending / Amount", value: `${fmtNum(prevPending)} / ${fmtCr(overdueRevisedPendingAmt)}`, sub: "prior to selected period", colour: "kpi-red", isItalic: true },
];

//  component 
const CustomMultiValue = (props) => {
  const { index, getValue } = props;
  const maxToShow = 2;
  const overflow = getValue().length - maxToShow;

  if (index < maxToShow) {
    return <components.MultiValue {...props} />;
  }

  if (index == maxToShow) {
    return (
      <div style={{ padding: "2px 8px", fontSize: "12px", fontWeight: "600", color: "#5a5278", display: "flex", alignItems: "center", background: "#eef0f6", borderRadius: "4px", margin: "2px" }}>
        +{overflow} more
      </div>
    );
  }

  return null;
};

// Live clock component: displays the current time and updates every second
const LiveClock = () => {
  const [timeStr, setTimeStr] = useState("");

  useEffect(() => {
    const updateTime = () => {
      setTimeStr(new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  return <div className="pd-clock">{timeStr}</div>;
};

const ProductionDashboard = () => {
  const { isLoading, setIsLoading } = useLoader();
  const [groupOptions, setGroupOptions]   = useState([]);
  const [selGroups, setSelGroups]         = useState([]);
  const [period, setPeriod]               = useState("weekly");
  const [fromDate, setFromDate]           = useState("");
  const [toDate, setToDate]               = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    selGroups: [],
    period: "weekly",
    fromDate: "",
    toDate: ""
  });

  // Store order-level data and summary/KPI data received from the dashboard API.
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false); // Hide filters by default on LED screen

  // Track the previous period to control the slide animation direction when switching weekly/monthly views. 
  const prevPeriodRef = useRef(appliedFilters.period);
  const [slideDir, setSlideDir] = useState("fwd");

  useEffect(() => {
    if (prevPeriodRef.current !== appliedFilters.period) {
      setSlideDir(appliedFilters.period === "monthly" ? "fwd" : "back");
      prevPeriodRef.current = appliedFilters.period;
    }
  }, [appliedFilters.period]);

  
  //  TV mode
  const [isPlaying, setIsPlaying] = useState(false);

  // Parse userData to get a unique identifier (userId or department)
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const userKey = userData.userId || "default";

  //  weekly and monthly target
  const [weeklyTarget, setWeeklyTarget] = useState(() => localStorage.getItem(`pd-weekly-target-${userKey}`) || "");
  const [monthlyTarget, setMonthlyTarget] = useState(() => localStorage.getItem(`pd-monthly-target-${userKey}`) || "");

  // local storage for weekly and monthly target
  useEffect(() => {
    localStorage.setItem(`pd-weekly-target-${userKey}`, weeklyTarget);
  }, [weeklyTarget, userKey]);

  useEffect(() => {
    localStorage.setItem(`pd-monthly-target-${userKey}`, monthlyTarget);
  }, [monthlyTarget, userKey]);

  // Auto-scrolling (TV Mode) refs and speed/timing configurations
  const timerRef = useRef(null);
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const SCROLL_PX_PER_SEC = 42;
  const DWELL_AT_END_MS = 2000;

  //  fetch dashboard data 
  const fetchData = useCallback(async (isAutoRefresh = false) => {
    // Only show loading shimmer if it's a manual refresh/filter change
    if (!isAutoRefresh) {
      setLoading(true);
      setIsLoading(true);
    }
    
    try {
      const payload = {
        prod_groups: appliedFilters.selGroups.map((g) => g.value),
        period: appliedFilters.period,
        ...(appliedFilters.period === "custom" ? { fromDate: appliedFilters.fromDate, toDate: appliedFilters.toDate } : {}),
      };
      const r = await axiosPrivate.post("dashboard/production_dashboard", payload);

      if (r.status === 200) {
        setRows(r.data.data || []);
        setSummary(r.data.summary || null);
        // Populate group filter dropdown from main API response (only on first / manual load)
        if (!isAutoRefresh && r.data.prod_groups?.length) {
          setGroupOptions((r.data.prod_groups || []).map((g) => ({ value: g.name, label: g.name })));
        }
      } else {
        setRows([]);
        setSummary(r.data?.summary || null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setRows([]);
        setSummary(err.response?.data?.summary || null);
      } else {
        if (!isAutoRefresh) showErrorToast(err?.response?.data?.message || err.message);
      }
    } finally {
      if (!isAutoRefresh) {
        setLoading(false);
        setIsLoading(false);
      }
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchData(false); // Initial load
    timerRef.current = setInterval(() => fetchData(true), AUTO_REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchData]);

  // TV Mode (Vertical Scrolling) useEffect
  useEffect(() => {
    let timerId;
    let rAFId;

    if (trackRef.current) {
      trackRef.current.style.transition = 'none';
      trackRef.current.style.transform = 'none';
    }

    if (!isPlaying) return;
    let lastTime = performance.now();
    let accumulatedScroll = 0;
    let isDwelling = false;

    // 1. If track is empty -> Just advance to next period after dwell
    // 2. If track is full -> Scroll until bottom, then advance
    const scrollLoop = (time) => {
      if (isDwelling) return;

      const container = viewportRef.current;
      if (!container) return;

      const maxScroll = container.scrollHeight - container.clientHeight;

      // if no scroll needed, dwell and advance
      if (maxScroll <= 0) {
        isDwelling = true;
        timerId = setTimeout(() => {
          const nextPeriod = appliedFilters.period === "weekly" ? "monthly" : "weekly";
          setPeriod(nextPeriod);
          setAppliedFilters(prev => ({ ...prev, period: nextPeriod }));
        }, DWELL_AT_END_MS + 2000);
        return;
      }

      // reached bottom
      if (Math.ceil(container.scrollTop) >= maxScroll) {
        isDwelling = true;
        timerId = setTimeout(() => {
          container.scrollTo(0, 0);
          const nextPeriod = appliedFilters.period === "weekly" ? "monthly" : "weekly";
          setPeriod(nextPeriod);
          setAppliedFilters(prev => ({ ...prev, period: nextPeriod }));
        }, DWELL_AT_END_MS);
        return;
      }

      const dt = time - lastTime;
      lastTime = time;

      accumulatedScroll += (dt / 1000) * SCROLL_PX_PER_SEC;

      if (accumulatedScroll >= 1) {
        const oldScroll = container.scrollTop;
        container.scrollTop = oldScroll + accumulatedScroll;
        // Subtract only what was actually scrolled by the browser to avoid losing fractional pixels over time
        accumulatedScroll -= (container.scrollTop - oldScroll);
      }

      rAFId = requestAnimationFrame(scrollLoop);
    };

    timerId = setTimeout(() => {
      lastTime = performance.now();
      rAFId = requestAnimationFrame(scrollLoop);
    }, 500);

    return () => {
      clearTimeout(timerId);
      cancelAnimationFrame(rAFId);
    };
  }, [isPlaying, appliedFilters.period, loading]);

  const handleApply = () => {
    setAppliedFilters({
      selGroups,
      period,
      fromDate,
      toDate
    });
  };

  const handleClear = () => {
    setSelGroups([]);
    setPeriod("weekly");
    setFromDate("");
    setToDate("");
    setAppliedFilters({
      selGroups: [],
      period: "weekly",
      fromDate: "",
      toDate: ""
    });
  };

  //  derived UI helpers 
  const { previousPendingCount, previousRevisedPendingAmt } = React.useMemo(() => {
    let previousPendingCount = 0;
    let previousRevisedPendingAmt = 0;
    const isWeeklyOrMonthly = summary?.period === "weekly" || summary?.period === "monthly";

    if (isWeeklyOrMonthly && summary?.fromDate) {
      for (const row of rows) {
        const d = row.promise_date || row.target_date || row.DueDt;
        if (!d) continue;
        const dStr = d.substring(0, 10);
        if (dStr < summary.fromDate) {
          previousPendingCount++;
          previousRevisedPendingAmt += parseFloat(row?.revisedPendingAmt ? row?.revisedPendingAmt : row?.PendingAmt) || 0;
        }
      }
    }
    return { previousPendingCount, previousRevisedPendingAmt };
  }, [rows, summary?.period, summary?.fromDate]);

  const kpis = React.useMemo(() => {
    return summary ? makeKpis(summary, previousPendingCount, previousRevisedPendingAmt) : [];
  }, [summary, previousPendingCount, previousRevisedPendingAmt]);

  const badgeText = summary
    ? `${fmtDate(summary.fromDate)} - ${fmtDate(summary.toDate)}`
    : appliedFilters.period;

  // table content with scroll effect
  const tableContent = React.useMemo(() => {
    const formatShortGroup = (grp) => {
      if (!grp) return "—";
            
      // If the group name is already short enough, keep it as is
      if (grp.length <= 11) return grp.toUpperCase(); 

      const words = grp.split(/[\s-]+/);
      
      if (words.length === 1) {
        // Single long word: take the first 4 characters
        return grp.substring(0, 4).toUpperCase();
      } else {
        // Multiple words: generate an acronym taking the first letter of each word
        let acronym = "";
        words.forEach(word => {
          if (word === "&") {
            acronym += "&";
          } else {
            const match = word.match(/[a-zA-Z0-9]/);
            if (match) acronym += match[0].toUpperCase();
          }
        });
        return acronym;
      }
    };

    const renderTableRows = (dataSlice, startIndex) => (
      dataSlice.map((row, i) => {
        const idx = startIndex + i;
        const promiseDate = row.promise_date || row.target_date || row.DueDt;

        // Highlight past pending jobs if they are before the selected fromDate
        let isPrevPending = false;
        const isWeeklyOrMonthly = summary?.period === "weekly" || summary?.period === "monthly";

        if (isWeeklyOrMonthly && summary?.fromDate && promiseDate) {
          const dStr = promiseDate.substring(0, 10);
          if (dStr < summary.fromDate) {
            isPrevPending = true;
          }
        }

        return (
          <tr key={idx} className={isPrevPending ? "pd-row-prev-pending" : ""}>
            <td className="cell-sr" style={{ width: "7%" }}>{idx + 1}</td>
            <td className="cell-grp" style={{ width: "10%" }} title={row.ProdGroups || ""}>
              {formatShortGroup(row.ProdGroups)}
            </td>
            <td className="cell-order" style={{ width: "15%" }}>{row.OrderNum || "—"}</td>
            <td className="cell-cust" title={row.CustomerName} style={{ width: "24%" }}>{row.CustomerName || "—"}</td>
            <td className="cell-num cell-qty" style={{ width: "15%" }}>{fmtNum(row.PendingQty)}</td>
            <td className="cell-date" style={{ width: "15%" }}>{fmtDate(promiseDate)}</td>
          </tr>
        );
      })
    );

    const mid = Math.ceil(rows.length / 2);
    return (
      <div style={{ display: 'flex', width: '100%', gap: '16px', alignItems: 'flex-start' }}>
        <table className="pd-table" style={{ tableLayout: 'fixed', width: '100%', borderTop: 'none', flex: 1 }}>
          <tbody>
            {renderTableRows(rows.slice(0, mid), 0)}
          </tbody>
        </table>
        <table className="pd-table" style={{ tableLayout: 'fixed', width: '100%', borderTop: 'none', flex: 1 }}>
          <tbody>
            {renderTableRows(rows.slice(mid), mid)}
          </tbody>
        </table>
      </div>
    );
  }, [rows, summary?.period, summary?.fromDate]);

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="pd-page">

          {/*  HEADER  */}
          <header className="pd-header">

            {/* LEFT SECTION */}
            <div className="pd-header-middle" style={{ justifyContent: "flex-start" }}>
              <h5 className="pd-header-title">
                {appliedFilters.selGroups.length > 0
                  ? appliedFilters.selGroups.slice(0, 3).map(g => g.label).join(" • ") + (appliedFilters.selGroups.length > 3 ? ` (+${appliedFilters.selGroups.length - 3})` : "")
                  : "ALL PRODUCTION GROUPS"
                }
              </h5>
            </div>

            {/* CENTER SECTION */}
            <div >
              {(appliedFilters.period === "weekly" || appliedFilters.period === "monthly") && (
                <div className="pd-target-pill">
                  <label>
                    {appliedFilters.period.toUpperCase()} TARGET ₹
                  </label>
                  <input
                    type="number"
                    value={appliedFilters.period === "weekly" ? weeklyTarget : monthlyTarget}
                    onChange={(e) => {
                      if (appliedFilters.period === "weekly") setWeeklyTarget(e.target.value);
                      else setMonthlyTarget(e.target.value);
                    }}
                    placeholder="0"
                  />
                </div>
              )}
            </div>

            {/* RIGHT SECTION */}
            <div className="pd-header-right" style={{ flex: 1, justifyContent: "flex-end" }}>
              <button onClick={() => setIsPlaying(!isPlaying)} className="pd-btn-icon" style={{ marginRight: "4px", color: isPlaying ? "var(--brand-red)" : "var(--brand-green)" }} title={isPlaying ? "Pause TV Mode" : "Start TV Mode"}>
                {isPlaying ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M5.5 3.5A1.5 1.5 0 0 1 7 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5zm5 0A1.5 1.5 0 0 1 12 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5z" /></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z" /></svg>
                )}
              </button>
              <div className="pd-live-badge">
                <span className="pd-live-dot" />
                LIVE
              </div>
              {summary && (
                <div className="pd-badge-range">
                  <strong className="text-green-900">{summary.period?.toUpperCase()}</strong>
                  <span>{badgeText}</span>
                </div>
              )}
              <LiveClock />
              <button className="pd-btn-icon" onClick={() => setShowFilter(!showFilter)} title="Toggle Filters">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M6 10.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z" />
                </svg>
              </button>
            </div>
          </header>

          {/*  FILTER BAR  */}
          {showFilter && (
            <div className="pd-filter-bar">
              <div className="pd-filter-group wide">
                <label className="pd-filter-label">Production Groups</label>
                <div className="pd-rs">
                  <Select
                    isMulti
                    options={groupOptions}
                    value={selGroups}
                    onChange={setSelGroups}
                    placeholder="All groups…"
                    classNamePrefix="react-select"
                    isClearable
                    components={{ MultiValue: CustomMultiValue }}
                  />
                </div>
              </div>
              <div className="pd-filter-group">
                <label className="pd-filter-label">Period</label>
                <select className="pd-select" value={period} onChange={(e) => setPeriod(e.target.value)}>
                  {PERIOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              {period === "custom" && (
                <>
                  <div className="pd-filter-group">
                    <label className="pd-filter-label">From Date</label>
                    <input type="date" className="pd-date-input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                  </div>
                  <div className="pd-filter-group">
                    <label className="pd-filter-label">To Date</label>
                    <input type="date" className="pd-date-input" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                  </div>
                </>
              )}
              <div className="pd-filter-actions">
                <button className="pd-btn pd-btn-apply" onClick={handleApply} disabled={loading}>
                  {loading ? "Loading…" : "Apply Filter"}
                </button>
                <button className="pd-btn pd-btn-clear" onClick={handleClear}>✕ Clear</button>
              </div>
            </div>
          )}

          <div className="pd-slide-viewport">
            <div
              key={appliedFilters.period}
              className={`pd-slide-panel ${slideDir === "back" ? "pd-slide-back" : ""}`}
            >
              {/* ] KPI CARDS  */}
              <div className="pd-kpi-strip">
                {kpis.map((k) => (
                  <div key={k.label} className={`pd-kpi ${k.colour}`}>
                    <div className="pd-kpi-body">
                      <div className="pd-kpi-label">{k.label}</div>
                      <div className="pd-kpi-value" style={k.isItalic ? {
                        fontFamily: 'Arial',
                        fontStyle: 'italic'
                      } : {}}>{k.value}</div>
                      <div className="pd-kpi-sub">{k.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/*  ORDER DETAIL TABLE  */}
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', paddingBottom: '12px' }}>
                <div className="pd-section-head" style={{ padding: '16px 24px 0', marginBottom: '10px', flexShrink: 0 }}>
                  <span className="pd-section-title">Order Details (Promise Date based)</span>
                  {!loading && rows.length > 0 && (
                    <span className="pd-count-badge">{rows.length} records</span>
                  )}
                </div>

                <div className="pd-table-section" style={{ paddingTop: 0, paddingBottom: 0, flex: 1 }}>

                  {loading ? (
                    <div className="pd-loading-skeleton">
                      <div className="pd-skeleton-bar" style={{ width: "60%", height: "18px", marginBottom: "12px" }} />
                      <div className="pd-skeleton-bar" style={{ width: "80%", height: "14px", marginBottom: "8px" }} />
                      <div className="pd-skeleton-bar" style={{ width: "40%", height: "14px", marginBottom: "8px" }} />
                      <div className="pd-skeleton-bar" style={{ width: "70%", height: "14px", marginBottom: "8px" }} />
                      <div className="pd-skeleton-bar" style={{ width: "55%", height: "14px" }} />
                    </div>
                  ) : rows.length === 0 ? (
                    <div className="pd-empty">
                      <div className="pd-empty-icon">📭</div>
                      <div className="pd-empty-title">No data found</div>
                      <div className="pd-empty-sub">
                        No orders found for the selected filters and date range.<br />
                        Try adjusting the filters or selecting a different period.
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

                      <div style={{ display: 'flex', width: '100%', gap: '16px' }}>
                        <table className="pd-table" style={{ tableLayout: 'fixed', width: '100%', marginBottom: 0, borderBottom: 'none', flex: 1 }}>
                          <thead>
                            <tr>
                              <th style={{ width: "7%" }}>S.No</th>
                              <th style={{ width: "10%" }}>Group</th>
                              <th style={{ width: "15%" }}>Order No.</th>
                              <th style={{ width: "24%" }}>Customer Name</th>
                              <th className="cell-num" style={{ width: "15%" }}>Pending Qty</th>
                              <th style={{ width: "15%" }}>Promise Date</th>
                            </tr>
                          </thead>
                        </table>
                        <table className="pd-table" style={{ tableLayout: 'fixed', width: '100%', marginBottom: 0, borderBottom: 'none', flex: 1 }}>
                          <thead>
                            <tr>
                              <th style={{ width: "7%" }}>S.No</th>
                              <th style={{ width: "10%" }}>Group</th>
                              <th style={{ width: "15%" }}>Order No.</th>
                              <th style={{ width: "24%" }}>Customer Name</th>
                              <th className="cell-num" style={{ width: "15%" }}>Pending Qty</th>
                              <th style={{ width: "15%" }}>Promise Date</th>
                            </tr>
                          </thead>
                        </table>
                      </div>

                      <div className="pd-ticker-viewport" ref={viewportRef} style={{ flex: 1, position: 'relative', overflowY: 'auto', overflowX: 'hidden' }}>
                        <div className="pd-ticker-track" ref={trackRef} style={{ position: 'relative', width: '100%', top: 0, left: 0 }}>

                          {tableContent}

                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

        </div>
      )}
    </>
  );
};

export default ProductionDashboard;
